import { createFeature, createReducer, on } from '@ngrx/store';
import {
  runtimeBridgeUpdate,
  startNativeClientSession,
} from '../actions/native-clients.actions';

import { logoutOAuth } from '../actions/session-auth.actions';
import {
  bookedAppointments,
  clientConfigFetched,
  dataPrivacyConfigFetched,
  isNativeClientDefined,
  userInfoFetched,
} from '../actions/session-info.actions';
import { AccountTerminationChanged } from '../actions/user-account.actions';
import { Appointment } from 'fhir/r4';
import { AUTHENTICATION_TYPE } from '@xt/domain-model';

export const enum USER_ROLE {
  RECORD_OWNER = 'record_owner',
  PRIVATE_RECORD = 'private_record',
  PATIENT = 'patient',
  PROVIDER = 'provider',
  DATA_PRIVACY_OFFICER = 'DATA_PRIVACY_OFFICER',
  DPO = 'data_privacy_officer',
  REPRESENTATIVE = 'representative',
  HCP = 'hcp',
  VERIFIED_PROVIDER = 'verified_provider',
  VERIFIED_REPRESENTATIVE = 'xt_representative',
  QUESTIONNAIRE_EDITOR = 'questionnaire_editor',
  PROVIDER_VERIFIER = 'verifier_provider',
  REPRESENTATIVE_VERIFIER = 'verifier_representative',
}

export const verifiedUserRoles = [
  USER_ROLE.PATIENT,
  USER_ROLE.HCP,
  USER_ROLE.DPO,
  USER_ROLE.VERIFIED_REPRESENTATIVE,
  USER_ROLE.VERIFIED_PROVIDER,
];

export const verifierRoles = [
  USER_ROLE.PROVIDER_VERIFIER,
  USER_ROLE.REPRESENTATIVE_VERIFIER,
];

export const patientAndRepresentativeRoles = [
  USER_ROLE.PATIENT,
  USER_ROLE.VERIFIED_REPRESENTATIVE,
];

// Defines additional roles not related to verified and category roles
export enum USER_ADDITIONAL_ROLE {
  PRIVATE_RECORD = 'xt_private_record',
}

export const enum MFA_AVAILABILITY {
  NOT_AVAILABLE = 'NOT_AVAILABLE',
  AVAILABLE = 'AVAILABLE',
  IN_RECOVERY = 'IN_RECOVERY',
  UNKNOWN = 'UNKNOWN',
}

export enum LOGGING_MODES {
  TRACE = 'trace',
  DEVELOPMENT = 'development',
  PRODUCTION = 'production',
}

export type AccountTerminationStatus = {
  terminated: boolean;
  daysUntilDeletion: number | null;
};

export type UserInfo = {
  roles: USER_ROLE[];
  //TODO: NOT OPTIONAL
  mfa_availability?: MFA_AVAILABILITY;
  name: string;
  preferred_username?: string;
  email?: string;
  authentication_type?: AUTHENTICATION_TYPE;
  patient_ID?: string;
  subject?: string;
  provider_ID?: {
    root: string;
    extension: string;
  };
  organization_ID?: string;
};

export type ClientConfig = {
  patientRootId: string;
  accountPatientNamespace: string;
  documentSourceId: string;
  developmentMode: LOGGING_MODES | null;
};

export type DataPrivacyConfig = {
  monitoring: Monitoring[];
  reports: Reports[];
};
export type Monitoring = {
  description: string;
  logo: string;
  name: string;
  url: string;
};

export type Reports = {
  description: string;
  logo: string;
  name: string;
  url: string;
};
export type Documents = {
  berechtigungskonzept: string;
  sicherheit: string;
};
export type BaseURLS = {
  inactive_user: string;
  user_search: string;
};

export type SessionKeys = {
  payload: string;
  user_role: USER_ROLE;
  databaseKey: string;
  matrix_id: string;
};

export const SESSION_INFO_STATE_KEY = 'sessionInfo';

export type SessionInfoState = {
  userInfo: UserInfo | null;
  accountTerminationStatus: AccountTerminationStatus;
  clientConfig: ClientConfig | null;
  sessionKeys: SessionKeys | null;
  isNativeClient: boolean | null;
  appointments: Appointment[] | null;
  consents: any;
};

const initialState: SessionInfoState = {
  userInfo: null,
  accountTerminationStatus: null,
  clientConfig: null,
  sessionKeys: null,
  isNativeClient: null,
  appointments: null,
  consents: null,
};

const reducer = createReducer(
  initialState,
  on(logoutOAuth, () => initialState),
  on(userInfoFetched, (state, { userInfo }) => ({
    ...state,
    userInfo,
  })),
  on(AccountTerminationChanged, (state, { accountTerminationStatus }) => ({
    ...state,
    accountTerminationStatus,
  })),
  on(clientConfigFetched, (state, { clientConfig }) => ({
    ...state,
    clientConfig,
  })),
  on(dataPrivacyConfigFetched, (state, { dataPrivacyConfig }) => ({
    ...state,
    dataPrivacyConfig,
  })),
  on(runtimeBridgeUpdate, (state, { authentication_type }) => {
    const oldUserInfo = state?.userInfo || { name: '', roles: [] };
    const userInfo = {
      ...oldUserInfo,
      authentication_type,
    };

    return {
      ...state,
      userInfo,
    };
  }),
  on(
    startNativeClientSession,
    (
      state,
      {
        payload,
        user_role,

        databaseKey,
        matrix_id,
      },
    ) => {
      const sessionKeys = {
        ...state,
        payload,
        user_role,
        databaseKey,
        matrix_id,
      };

      return {
        ...state,
        sessionKeys,
      };
    },
  ),

  on(isNativeClientDefined, (state, { isNativeClient }) => {
    return {
      ...state,
      isNativeClient,
    };
  }),

  on(bookedAppointments, (state, { appointments }) => {
    return {
      ...state,
      appointments,
    };
  }),
);

export const sessionInfoFeature = createFeature({
  name: SESSION_INFO_STATE_KEY,
  reducer,
});
